import { useEffect, useState } from 'react';
import { isNil } from 'lodash';

import { CATEGORY_LIST_FILTER_OPTION_MODEL, CATEGORY_LIST_PAGINATION_MODEL } from '../../store/modules/category/models';
import {
  CATEGORY_EXHIBIT_TYPE_SELECT_LIST,
  CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST,
  CATEGORY_SEARCH_KEYWORD_TYPE,
  CATEGORY_SEARCH_MAIN_TYPE,
  CATEGORY_SELECT_INITIAL_TYPE_SELECT_LIST,
  CATEGORY_SORT_TYPE_SELECT_LIST,
  CATEGORY_STATUS_TYPE_SELECT_LIST,
  CATEGORY_TYPE_SELECT_LIST,
} from '../../constants/service/app/categoryConstants';
import useCategoryListRouter from './useCategoryListRouter';

/**
 * 카테고리 검색 필터에 사용되는 로직을 담고있는 custom hook
 * @param form
 * @param {object} filterOption
 * @return {{onSubmit: onSubmit, categorySubOptionList: {name: string, id: number}[], onReset: onReset, onChangeDetailOption: onChangeDetailOption, routeByFilterOption: routeByFilterOption}}
 */
const useCategorySearchFilter = (form, filterOption) => {
  /**
   * 카테고리 검색 Input 비활성화 여부
   */
  const [isDisabled, setIsDisabled] = useState(false);

  /**
   * 카테고리 검색 필터 옵션을 기반으로 만든 쿼리스트링으로 라우팅하는 함수
   */
  const { routeByFilterOption } = useCategoryListRouter({ filterOption });

  // 카테고리 메인 옵션 선택에 따른 서브 옵션 리스트 KeyMap
  const optionListKeyMap = {
    // 전체 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.ENTIRE]: CATEGORY_SELECT_INITIAL_TYPE_SELECT_LIST,
    // 카테고리 종류 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.KIND]: CATEGORY_TYPE_SELECT_LIST,
    // 활성 여부 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.STATUS]: CATEGORY_STATUS_TYPE_SELECT_LIST,
    // 전시 여부 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.EXHIBIT]: CATEGORY_EXHIBIT_TYPE_SELECT_LIST,
    // 노출 위치 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.EXPOSE]: CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST,
    // 상품 정렬 순 옵션을 골랐을 때
    [CATEGORY_SEARCH_MAIN_TYPE.SORT]: CATEGORY_SORT_TYPE_SELECT_LIST,
  };

  // 카테고리 검색 키워드 옵션 KeyMap
  const keywordOptionKeyMap = {
    [CATEGORY_SEARCH_KEYWORD_TYPE.ID]: 'id',
    [CATEGORY_SEARCH_KEYWORD_TYPE.NAME]: 'name',
  };

  // 카테고리 검색 1차 옵션 KeyMap
  const mainOptionKeyMap = {
    [CATEGORY_SEARCH_MAIN_TYPE.KIND]: 'type',
    [CATEGORY_SEARCH_MAIN_TYPE.STATUS]: 'status',
    [CATEGORY_SEARCH_MAIN_TYPE.EXHIBIT]: 'isExhibit',
    [CATEGORY_SEARCH_MAIN_TYPE.EXPOSE]: 'exposureType',
    [CATEGORY_SEARCH_MAIN_TYPE.SORT]: 'sortType',
  };

  /**
   * 필터 옵션의 카테고리 ID와 카테고리 명이 없고, 현재 키워드 옵션 form 상태가 선택 옵션인 경우 검색 Input disabled 처리
   */
  useEffect(() => {
    setIsDisabled(
      !filterOption.id &&
        !filterOption.name &&
        form.getFieldValue('keyword-option') === CATEGORY_SEARCH_KEYWORD_TYPE.NONE,
    );
  }, [filterOption]);

  /**
   * 검색 키워드 옵션 필터를 바꿀 때
   */
  const onChangeKeywordOption = (type) => {
    /**
     * 검색 키워드 값 초기화
     */
    form.setFieldsValue({ keyword: null });

    /**
     * 선택 옵션을 고를 시 검색 키워드 input disabled 처리
     */
    if (type === CATEGORY_SEARCH_KEYWORD_TYPE.NONE) {
      setIsDisabled(true);
      return;
    }
    setIsDisabled(false);
  };

  /**
   * 카테고리 검색 버튼 클릭 시
   */
  const onSubmit = () => {
    form.validateFields((err, values) => {
      if (err) return;

      // 카테고리 검색 키워드 값
      const keywordOptionValue = values['keyword-option'];
      // 카테고리 검색 1차 옵션 Value
      const mainOptionKey = values['main-option'];

      /**
       * 검색 키워드 필터 옵션 정보를 반환하는 함수
       * @return {{ id?: number, name?: string }}
       */
      const getKeywordFilterOption = () => {
        // 검색 키워드 옵션을 선택하지 않았을 때 필터 옵션에 추가하지 않음
        if (keywordOptionValue === CATEGORY_SEARCH_KEYWORD_TYPE.NONE) return;

        // 검색 키워드 옵션이 카테고리 id이거나 카테고리 이름이고, 해당 키워드 값이 빈 문자열, null, undefined일 경우 필터 옵션에 추가하지 않음
        if (
          (keywordOptionValue === CATEGORY_SEARCH_KEYWORD_TYPE.ID ||
            keywordOptionValue === CATEGORY_SEARCH_KEYWORD_TYPE.NAME) &&
          (values['keyword'] === '' || isNil(values['keyword']))
        )
          return;

        return {
          [keywordOptionKeyMap[keywordOptionValue]]: values['keyword'],
        };
      };

      /**
       * 검색 카테고리 필터 옵션 정보를 반환하는 함수
       * @return {{ type?: number, status?: number, isExhibit?: number, exposureType?: number, sortType?: number}}
       */
      const getCategoryFilterOption = () => {
        // 카테고리 메인 필터 옵션을 선택하지 않았을 때 필터 옵션에 추가하지 않음
        if (mainOptionKey === CATEGORY_SEARCH_MAIN_TYPE.ENTIRE) return;

        // 카테고리 서브 필터 옵션 값이 null, undefined일 경우 필터 옵션에 추가하지 않음
        if (isNil(values['sub-option'])) return;

        return {
          [mainOptionKeyMap[mainOptionKey]]: values['sub-option'],
        };
      };
      /**
       * @typedef {Object} filterOptionType - 카테고리 검색 필터 옵션 타입
       * @property {number} [id] - 카테고리 검색 키워드 타입(id)
       * @property {string} [name] - 카테고리 검색 키워드 타입(name)
       * @property {number} [type] - 카테고리 종류
       * @property {number} [status] - 카테고리 활성 여부
       * @property {number} [isExhibit] - 카테고리 전시 여부
       * @property {number} [exposureType] - 카테고리 노출 위치
       * @property {number} [sortType] - 카테고리 상품 정렬 순
       * @property {number} page - 검색 페이지
       * @property {number} pageSize - 검색 페이지 사이즈
       * @property {number} degree - 카테고리 차수
       * 카테고리 검색 필터 form state에 따라서 filterOption을 생성 후 해당 filterOption을 기반으로 만든 queryString url로 라우팅
       */
      const filterOption = {
        ...getKeywordFilterOption(),
        ...getCategoryFilterOption(),
        page: 1,
        pageSize: CATEGORY_LIST_PAGINATION_MODEL.pageSize,
        degree: 1,
      };
      routeByFilterOption(filterOption);
    });
  };

  /**
   * 카테고리 검색 필터 초기화 버튼 클릭 시
   */
  const onReset = () => {
    // form state 초기화
    form.setFieldsValue({ 'keyword-option': CATEGORY_SEARCH_KEYWORD_TYPE.NONE });
    form.setFieldsValue({ keyword: null });
    form.setFieldsValue({ 'main-option': CATEGORY_SEARCH_MAIN_TYPE.ENTIRE });
    form.setFieldsValue({ 'sub-option': CATEGORY_SEARCH_MAIN_TYPE.ENTIRE });

    // 초기화 filterOption 값으로 쿼리스트링을 만들어 라우팅
    routeByFilterOption({
      ...CATEGORY_LIST_FILTER_OPTION_MODEL,
      page: 1,
      pageSize: CATEGORY_LIST_PAGINATION_MODEL.pageSize,
    });
  };

  return {
    optionListKeyMap,
    onChangeKeywordOption,
    isDisabled,
    setIsDisabled,
    onSubmit,
    onReset,
  };
};

export default useCategorySearchFilter;
